import React, { useMemo, useState } from 'react';

import { Button, Chip, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useLocation } from 'react-router-dom';

import processing_activities_messages from 'messages/processing_activities_messages';
import PATHS from 'router/PATHS';

import type { ProcessingActivityDetailPurpose } from '../processingActivityDetail.types';
import useStyles from '../ProcessingActivityDetailPage.styles';

type Props = {
  purposes: ProcessingActivityDetailPurpose[];
};

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

const getLegalBasisLabel = (code: string, t: (message: string | string[]) => string): string => {
  const withArticle = processing_activities_messages.editor.legal_basis_with_article as Record<string, string[]>;
  if (withArticle[code]) {
    return t(withArticle[code]);
  }

  return getMessageFromMap(processing_activities_messages.legal_basis, code, t);
};

const getPurposeSummary = (purpose: ProcessingActivityDetailPurpose, t: (message: string | string[]) => string): string => {
  const legalBasis = purpose.legalBasis ? getLegalBasisLabel(purpose.legalBasis, t) : t(processing_activities_messages.review_placeholder);
  if (purpose.liaRequired) {
    return `${legalBasis} · ${t(processing_activities_messages.editor.lia_required_chip)}`;
  }

  return legalBasis;
};

const ProcessingActivityPurposesReadSection: React.FC<Props> = ({ purposes }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [showRemovedPurposes, setShowRemovedPurposes] = useState(false);

  const visiblePurposes = useMemo(() => {
    if (showRemovedPurposes) {
      return purposes;
    }

    return purposes.filter(purpose => !purpose.isDeleted);
  }, [purposes, showRemovedPurposes]);

  const activePurposesCount = purposes.filter(purpose => !purpose.isDeleted).length;

  const handleOpenLia = (purposeId: number) => {
    history.push({
      pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_PURPOSE_LIA, { purposeId: String(purposeId) }),
      state: { returnPath: location.pathname },
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.sectionHeader}>
        <div>
          <Typography className={styles.panelTitle} component='h2' variant='h6'>
            {t(processing_activities_messages.detail.sections.purposes)}
          </Typography>
          <div className={styles.sectionHelp}>{t(processing_activities_messages.detail.purposes.help)}</div>
        </div>
        <label className={styles.toggleLine} htmlFor='show-removed-purposes'>
          <input
            checked={showRemovedPurposes}
            id='show-removed-purposes'
            onChange={event => setShowRemovedPurposes(event.target.checked)}
            type='checkbox'
          />
          <span>{t(processing_activities_messages.detail.purposes.show_removed)}</span>
        </label>
      </div>

      {activePurposesCount === 0 && !showRemovedPurposes ? (
        <Typography className={styles.emptyState} color='textSecondary' variant='body2'>
          {t(processing_activities_messages.detail.empty_state.no_active_purposes)}
        </Typography>
      ) : (
        <div className={styles.purposeStack}>
          {visiblePurposes.map((purpose, index) => (
            <details
              key={purpose.id ?? `${purpose.purposeText}-${index}`}
              className={clsx(styles.purposeCard, purpose.isDeleted && styles.removedPurpose)}
            >
              <summary className={styles.purposeSummary}>
                <span className={styles.purposeMain}>{purpose.purposeText || t(processing_activities_messages.review_placeholder)}</span>
                <span className={styles.purposeMeta}>{getPurposeSummary(purpose, t)}</span>
              </summary>
              <div className={styles.purposeContent}>
                <div className={clsx(styles.readField, styles.readFieldFull)}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.purpose)}</div>
                  <div className={styles.readValue}>{purpose.purposeText || t(processing_activities_messages.review_placeholder)}</div>
                </div>
                <div className={styles.readField}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.legal_basis)}</div>
                  <div className={styles.readValue}>
                    {purpose.legalBasis ? getLegalBasisLabel(purpose.legalBasis, t) : t(processing_activities_messages.review_placeholder)}
                  </div>
                </div>
                {purpose.isDeleted && (
                  <div className={styles.readField}>
                    <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.purpose_status)}</div>
                    <Chip className={styles.removedBadge} label={t(processing_activities_messages.detail.purposes.removed)} size='small' />
                  </div>
                )}
                <div className={clsx(styles.readField, styles.readFieldFull)}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.legal_basis_comment)}</div>
                  <div className={styles.readValue}>
                    {purpose.legalBasisComment || t(processing_activities_messages.review_placeholder)}
                  </div>
                </div>
                <div className={styles.readField}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.lia_required)}</div>
                  <div className={styles.readValue}>
                    {purpose.liaRequired
                      ? t(processing_activities_messages.filters.boolean_options.true)
                      : t(processing_activities_messages.filters.boolean_options.false)}
                  </div>
                </div>
                <div className={styles.readField}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.linked_lia)}</div>
                  <div className={styles.readValue}>
                    {purpose.liaRequired && purpose.id ? (
                      <Button color='primary' onClick={() => handleOpenLia(purpose.id as number)} size='small'>
                        {t(processing_activities_messages.purpose_lia.actions.open)}
                      </Button>
                    ) : (
                      t(processing_activities_messages.detail.fields.linked_lia_not_required)
                    )}
                  </div>
                </div>
                <div className={clsx(styles.readField, styles.readFieldFull)}>
                  <div className={styles.readLabel}>{t(processing_activities_messages.detail.fields.retention_period)}</div>
                  <div className={styles.readValue}>
                    {purpose.retentionDescription || t(processing_activities_messages.review_placeholder)}
                  </div>
                </div>
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProcessingActivityPurposesReadSection;
